import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  ColumnData,
  EditAlertForm,
  NewAlertForm,
  SlideAction,
  SlideLayout,
  SlideLayoutLocationState,
  ViewMode,
} from '@gripp/shared-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutinePreviewColumn } from '../../new-routines/components/routinePreviewColumn';
import {
  AlertFormSlot,
  useAlertFormColumn,
} from '../../shared/components/alerts/alertFormPanel';
import { ProductAlertList } from './productAlertList';
import { ProductNewAlertHeaderAction } from './productNewAlertHeaderAction';

type AlertFormLocationState = SlideLayoutLocationState & {
  fromRoutine?: boolean;
} & Record<string, unknown>;

const toLocationState = (state: unknown): AlertFormLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AlertFormLocationState)
    : null;

// ─── Form content (right column) ─────────────────────────────────────────────

const AlertFormContent = ({
  productId,
  routineId,
  alertId,
  onBack,
  formRef,
  renderBottomComponent,
  onRegisterBackGuard,
}: AlertFormSlot & {
  productId: string;
  routineId: string;
  alertId?: string;
  onBack: () => void;
}) => {
  const currentWorkspace = useCurrentWorkspace();

  return alertId ? (
    <EditAlertForm
      key={alertId}
      alertId={alertId}
      routineId={routineId}
      productId={productId}
      onClose={onBack}
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      onRegisterBackGuard={onRegisterBackGuard}
    />
  ) : (
    <NewAlertForm
      routineId={routineId}
      workspaceAssetId=""
      productId={productId}
      workspace={currentWorkspace?.id}
      onClose={onBack}
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      onRegisterBackGuard={onRegisterBackGuard}
    />
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ProductAlertFormPage = () => {
  const { productId = '', routineId = '', alertId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const routineUrl = `/products/${productId}/routines/${routineId}`;
  const alertsUrl = `/products/${productId}/alerts/${routineId}`;
  const locationState = toLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const fromRoutine = locationState?.fromRoutine;
  const {
    fromRoutine: _fromRoutine,
    slideLayoutInitialTransition: _slideLayoutInitialTransition,
    ...backState
  } = locationState ?? {};

  const getStateWithTransition = (transition: SlideAction) => ({
    ...backState,
    slideLayoutInitialTransition: transition,
  });

  const rightColumn = useAlertFormColumn({
    title: alertId ? 'alerts.editAlert' : 'alerts.newAlert',
    onBack: () =>
      navigate(fromRoutine ? routineUrl : alertsUrl, {
        state: getStateWithTransition(SlideAction.PREV),
      }),
    renderForm: (slot) => (
      <AlertFormContent
        {...slot}
        productId={productId}
        routineId={routineId}
        alertId={alertId}
        onBack={() =>
          navigate(fromRoutine ? routineUrl : alertsUrl, {
            state: { ...backState },
          })
        }
      />
    ),
    column: {
      backButtonTransition: SlideAction.PREV,
      backButtonTransitionBehavior: 'concurrent',
    },
  });

  if (!productId || !routineId) return null;

  const leftColumn: ColumnData = fromRoutine
    ? {
        title: 'product.detail.routines',
        mainComponent: (
          <RoutinePreviewColumn routineId={routineId} basePath={routineUrl} />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      }
    : {
        title: 'alerts.alerts',
        mainComponent: (
          <ProductAlertList
            productId={productId}
            routineId={routineId}
            onAlertSelected={(alert) => {
              if (!alert?.id) return;
              navigate(
                `/products/${productId}/alerts/${routineId}/${alert.id}`
              );
            }}
          />
        ),
        headerComponent: (
          <ProductNewAlertHeaderAction
            productId={productId}
            routineId={routineId}
          />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
